import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const Attendance = () => {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [attendanceForm, setAttendanceForm] = useState({ jobSeekerId: '', status: '' });
  const [attendanceStats, setAttendanceStats] = useState([]);
  const [activityMetrics, setActivityMetrics] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setJobSeekers(data.jobSeekers);
        } else {
          setError(data.message || 'Failed to fetch dashboard');
        }
      } catch (err) {
        setError('Server error while fetching dashboard');
        console.error('Fetch dashboard error:', err);
      }
    };

    const fetchAttendanceStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/attendance-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setAttendanceStats(data.attendanceStats);
          setActivityMetrics(data.activityMetrics);
        } else {
          setError(data.message || 'Failed to fetch attendance stats');
        }
      } catch (err) {
        setError('Server error while fetching attendance stats');
        console.error('Fetch attendance stats error:', err);
      }
    };

    if (token) {
      fetchDashboard();
      fetchAttendanceStats();
    }
  }, [token]);

  const handleMarkAttendance = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/mark-attendance/${attendanceForm.jobSeekerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: attendanceForm.status })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        setAttendanceForm({ jobSeekerId: '', status: '' });
        // Refresh stats after marking attendance
        const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/attendance-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const statsData = await statsResponse.json();
        if (statsResponse.ok) {
          setAttendanceStats(statsData.attendanceStats);
          setActivityMetrics(statsData.activityMetrics);
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Server error while marking attendance');
      console.error('Mark attendance error:', err);
    }
  };

  // Pie chart data for total present vs. absent
  const totalPresent = attendanceStats.reduce((sum, stat) => sum + stat.attendance.present, 0);
  const totalAbsent = attendanceStats.reduce((sum, stat) => sum + stat.attendance.absent, 0);
  const pieChartData = {
    labels: ['Present', 'Absent'],
    datasets: [{
      data: [totalPresent, totalAbsent],
      backgroundColor: ['#F97316', '#374151'],
      borderColor: '#FFFFFF'
    }]
  };

  return (
    <div className="p-6 bg-white text-gray-800">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Admin Attendance Dashboard</h2>

      {/* Mark Attendance Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Mark JobSeeker Attendance</h3>
        <form onSubmit={handleMarkAttendance} className="bg-gray-100 p-6 rounded-lg shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <select
              value={attendanceForm.jobSeekerId}
              onChange={(e) => setAttendanceForm({ ...attendanceForm, jobSeekerId: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            >
              <option value="">Select JobSeeker</option>
              {jobSeekers.map(js => (
                <option key={js._id} value={js._id}>
                  {js.firstName} {js.lastName} ({js.email})
                </option>
              ))}
            </select>
            <select
              value={attendanceForm.status}
              onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            >
              <option value="">Select Status</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
            </select>
          </div>
          <button
            type="submit"
            className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
          >
            Mark Attendance
          </button>
          {success && <p className="mt-2 text-green-600">{success}</p>}
          {error && <p className="mt-2 text-red-600">{error}</p>}
        </form>
      </div>

      {/* Attendance Statistics Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Attendance Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-2 text-left text-gray-700">Name</th>
                  <th className="p-2 text-left text-gray-700">Email</th>
                  <th className="p-2 text-left text-gray-700">Present</th>
                  <th className="p-2 text-left text-gray-700">Absent</th>
                </tr>
              </thead>
              <tbody>
                {attendanceStats.map(stat => (
                  <tr key={stat.jobSeekerId} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-2">{stat.firstName} {stat.lastName}</td>
                    <td className="p-2">{stat.email}</td>
                    <td className="p-2">{stat.attendance.present}</td>
                    <td className="p-2">{stat.attendance.absent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {attendanceStats.length === 0 && (
              <p className="text-gray-600 mt-2">No attendance stats available.</p>
            )}
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-xs">
              <h4 className="text-lg font-semibold text-gray-800 mb-2 text-center">Total Attendance</h4>
              <Pie data={pieChartData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>
        </div>
      </div>

      {/* Activity Metrics Section */}
      {activityMetrics && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Activity Metrics</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-2 text-left text-gray-700">Period</th>
                  <th className="p-2 text-left text-gray-700">Count</th>
                  <th className="p-2 text-left text-gray-700">JobSeekers</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-2">Daily</td>
                  <td className="p-2">{activityMetrics.daily.count}</td>
                  <td className="p-2">{activityMetrics.daily.jobSeekers.map(js => js.email).join(', ') || 'None'}</td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-2">Weekly</td>
                  <td className="p-2">{activityMetrics.weekly.count}</td>
                  <td className="p-2">{activityMetrics.weekly.jobSeekers.map(js => js.email).join(', ') || 'None'}</td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-2">Monthly</td>
                  <td className="p-2">{activityMetrics.monthly.count}</td>
                  <td className="p-2">{activityMetrics.monthly.jobSeekers.map(js => js.email).join(', ') || 'None'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* JobSeekers List */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">My JobSeekers</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 text-left text-gray-700">Name</th>
                <th className="p-2 text-left text-gray-700">Email</th>
                <th className="p-2 text-left text-gray-700">Jobs Assigned</th>
              </tr>
            </thead>
            <tbody>
              {jobSeekers.map(js => (
                <tr key={js._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-2">{js.firstName} {js.lastName}</td>
                  <td className="p-2">{js.email}</td>
                  <td className="p-2">{js.jobAssignments?.length || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {jobSeekers.length === 0 && (
            <p className="text-gray-600 mt-2">No jobseekers found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Attendance;